import { DATA } from '../data';
import { UpgradeData } from '../data.type';
import { niceRounding } from '../helpers';
import {
  ADVANCE_COEFFICIENT,
  ADVANCE_PASSENGERS_COEFFICIENT,
  BASE_AIRPORT_PRICE,
  PRICE_STEP_COEFFICIENT,
  UPGRADE_PRICE_COEFFICIENT,
} from './baseData';
import { IMAGES } from './images';

export type AirportSlug =
  | 'lax'
  | 'jfk'
  | 'mex'
  | 'bsb'
  | 'scl'
  | 'fco'
  | 'lhr'
  | 'svo'
  | 'cpt'
  | 'hnd'
  | 'del'
  | 'cgk'
  | 'syd'
  | 'sfj'
  | 'ebb'
  | 'ovb';

export const AIRPORT_SLUGS: AirportSlug[] = [
  'lax',
  'jfk',
  'mex',
  'bsb',
  'scl',
  'fco',
  'lhr',
  'svo',
  'cpt',
  'hnd',
  'del',
  'cgk',
  'syd',
  'sfj',
  'ebb',
  'ovb',
];

export const DEFAULT_UNLOCKED_AIRPORTS = 3;

export const AIRPORT_LIST: {
  name: string;
  slug: AirportSlug;
  pos: { x: number; y: number };
}[] = [
  { name: 'New York', slug: 'jfk', pos: { x: 25, y: 30 } },
  { name: 'London', slug: 'lhr', pos: { x: 47, y: 25 } },
  { name: 'Rome', slug: 'fco', pos: { x: 51, y: 32 } },
  { name: 'Mexico', slug: 'mex', pos: { x: 13, y: 45 } },
  { name: 'Delhi', slug: 'del', pos: { x: 73, y: 40 } },
  { name: 'Moscow', slug: 'svo', pos: { x: 56, y: 24 } },
  { name: 'Brasilia', slug: 'bsb', pos: { x: 30, y: 65 } },
  { name: 'Entebbe', slug: 'ebb', pos: { x: 57, y: 59 } },
  { name: 'Kangerlussuaq', slug: 'sfj', pos: { x: 34, y: 18 } },
  { name: 'Novosibirsk', slug: 'ovb', pos: { x: 76, y: 24 } },
  { name: 'Los Angeles', slug: 'lax', pos: { x: 7.5, y: 36.5 } },
  { name: 'Jakarta', slug: 'cgk', pos: { x: 84, y: 61 } },
  { name: 'Santiago', slug: 'scl', pos: { x: 23, y: 78 } },
  { name: 'Cape Town', slug: 'cpt', pos: { x: 53, y: 76 } },
  { name: 'Sydney', slug: 'syd', pos: { x: 96, y: 77 } },
  { name: 'Tokyo', slug: 'hnd', pos: { x: 92, y: 35 } },
];

export type AirportUpgradeType = 'capacity' | 'arrival_speed' | 'ticket_price_multiplier';

export const AIRPORT_DATA = {
  priceFn: (unlockLevel: number) => {
    unlockLevel -= DEFAULT_UNLOCKED_AIRPORTS;
    if (unlockLevel < 0) return 0;
    return niceRounding(BASE_AIRPORT_PRICE * Math.pow(ADVANCE_COEFFICIENT + 0.15, unlockLevel));
  },
};

export const AIRPORT_UPGRADES: {
  [k in AirportUpgradeType]: UpgradeData;
} = {
  // from 25 to 1000, 25 increment
  capacity: {
    icon: IMAGES.ICONS.persons,
    description: 'Airport capacity',
    maxValue: 39,
    priceFn: (upgradeLevel, priceStep) =>
      niceRounding(100 * Math.pow(UPGRADE_PRICE_COEFFICIENT, upgradeLevel) * Math.pow(PRICE_STEP_COEFFICIENT, priceStep)),
    valueFn: (upgradeLevel) => Math.round(25 + upgradeLevel * 25),
    readableValue: (upgradeLevel) => `${AIRPORT_UPGRADES.capacity.valueFn(upgradeLevel)}`,
  },
  // how many passengers per second arrive. Value is 1, 2, 3... up to 50
  arrival_speed: {
    icon: IMAGES.ICONS.person,
    maxValue: 49,
    description: 'Passengers arrival speed',
    priceFn: (upgradeLevel, priceStep) =>
      niceRounding(100 * Math.pow(UPGRADE_PRICE_COEFFICIENT, upgradeLevel) * Math.pow(PRICE_STEP_COEFFICIENT, priceStep)),
    valueFn: (upgradeLevel) => Math.round(1 + upgradeLevel),
    readableValue: (upgradeLevel) => `${AIRPORT_UPGRADES.arrival_speed.valueFn(upgradeLevel)}/s`,
  },
  // value grows between 1..2, 0.05 increment
  ticket_price_multiplier: {
    icon: IMAGES.ICONS.ticket,
    maxValue: 50,
    description: 'Ticket price multiplier',
    priceFn: (upgradeLevel, priceStep) =>
      niceRounding(100 * Math.pow(UPGRADE_PRICE_COEFFICIENT, upgradeLevel) * Math.pow(PRICE_STEP_COEFFICIENT, priceStep)),
    valueFn: (upgradeLevel) => 1 + upgradeLevel * 0.02,
    readableValue: (upgradeLevel) => `x${AIRPORT_UPGRADES.ticket_price_multiplier.valueFn(upgradeLevel).toFixed(2)}`,
  },
};

export const POPULATION_INCREASE_EVENT_DURATION_MS = 30000;
/** Don't start sending passengers from full airports right away, do it after some time */
export const OFFLOAD_GRACE_PERIOD_MS = 0;
